import { useEffect, useState } from "react";

import type { Hit, SessionApi } from "../lib/api";

interface Props {
  api: SessionApi;
  onOpen: (path: string, line: number) => void;
}

const DEBOUNCE_MS = 250;

export function SearchPanel({ api, onOpen }: Props) {
  const [query, setQuery] = useState("");
  const [regex, setRegex] = useState(false);
  const [hits, setHits] = useState<Hit[]>([]);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    if (!query.trim()) {
      setHits([]);
      setStatus(null);
      return;
    }
    // A search per keystroke would run ripgrep over the tree on every letter.
    const timer = setTimeout(async () => {
      setStatus("searching…");
      try {
        const found = await api.search(query, regex);
        setHits(found);
        setStatus(found.length ? `${found.length} result${found.length === 1 ? "" : "s"}` : "no results");
      } catch (err) {
        setHits([]);
        setStatus(err instanceof Error ? err.message : String(err));
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [api, query, regex]);

  const byFile = hits.reduce<Record<string, Hit[]>>((acc, hit) => {
    (acc[hit.path] ??= []).push(hit);
    return acc;
  }, {});

  return (
    <div className="search-panel">
      <input
        className="search-input"
        placeholder="Search workspace"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        autoFocus
      />
      <label className="search-toggle">
        <input type="checkbox" checked={regex} onChange={(e) => setRegex(e.target.checked)} />
        regex
      </label>
      {status && <div className="search-status">{status}</div>}
      <div className="search-results">
        {Object.entries(byFile).map(([path, fileHits]) => (
          <div key={path}>
            <div className="search-file">{path}</div>
            {fileHits.map((hit) => (
              <button
                key={`${hit.path}:${hit.line}:${hit.column}`}
                className="search-hit"
                onClick={() => onOpen(hit.path, hit.line)}
                title={hit.text.trim()}
              >
                <span className="search-line">{hit.line}</span>
                <span className="search-text">{hit.text.trim().slice(0, 120)}</span>
              </button>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
